import React, { useState, useEffect, useRef } from 'react'
import { postAPI } from '../../../app/api';
import { Link, useNavigate } from 'react-router-dom';
import { store } from '../../../app/store';
import newsReport from "../../../assets/img/news-letter.pdf"
import logo from "../../../assets/img/logo-only.png"
import { Navigation, Autoplay } from 'swiper/modules';
import Swiper from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';

const { REACT_APP_BRANDURL } = process.env;

export default function NoticeSection() {
    const navigate = useNavigate();
    const [NoticeData, setNoticeData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    // Check login status
    useEffect(() => {
        const store_data = JSON.parse(store() || '{}');
        setIsLoggedIn(store_data?.is_login === 1);
    }, []);

    // Helper: categories that are fully visible to guests (no blur)
    const isFullyVisibleForGuest = (name) => {
        if (!name) return false;
        const n = name.toLowerCase();
        // "General Club Notice" and "Rules & Procedure" are public
        return n.includes('general club notice') || n.includes('general') || n.includes('rules & procedure') || n.includes('rules') || n.includes('procedure');
    };

    // Helper: categories that guests can see the tab but content is blurred (e.g., Club Report)
    const isBlurredForGuest = (name) => {
        if (!name) return false;
        const n = name.toLowerCase();
        return n.includes('club report') || n.includes('report');
    };

    useEffect(() => {
        const initFetch = async () => {
            try {
                setLoading(true);

                // Fetch categories first
                const catResponse = await postAPI('notice-category/list', {});
                if (catResponse?.status === 1) {
                    const sortedCategories = (catResponse?.data || []).sort((a, b) => {
                        if (a.name === 'General Club Notice') return -1;
                        if (b.name === 'General Club Notice') return 1;
                        return 0;
                    });
                    setCategories(sortedCategories);
                }

                // Fetch notices
                const response = await postAPI('notice/list-section-data', {});
                if (response?.status === 1) {
                    setNoticeData(response?.data || []);
                }
            } catch (e) {
                console.error('Error fetching notices:', e);
            } finally {
                setLoading(false);
            }
        };
        initFetch();
    }, []);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return {
            day: date.getDate().toString().padStart(2, '0'),
            month: date.toLocaleDateString('en-US', { month: 'short' }),
            year: date.getFullYear()
        };
    };

    const handleShowNotices = () => {
        navigate('/login');
    };

    const displayNotices = NoticeData;
    // For guests: show fully visible + blurred categories; for logged-in: show all
    const visibleCategories = isLoggedIn
        ? categories
        : (categories || []).filter(c => isFullyVisibleForGuest(c.name) || isBlurredForGuest(c.name));
    // If no allowed categories found for guest, fallback to any category that contains 'general' or the first category
    if (!isLoggedIn && visibleCategories.length === 0 && categories && categories.length > 0) {
        const fallback = categories.filter(c => c.name && c.name.toLowerCase().includes('general'));
        if (fallback.length > 0) {
            visibleCategories.push(...fallback);
        } else {
            // show first category as last resort
            visibleCategories.push(categories[0]);
        }
    }

    return (
        <>
            <section className="notices py-5">
                <div className="container-xxl container-xl container-fluid">
                    {/* Heading */}
                    <div className="text-center mb-5">
                        <h2>Notices & Announcements</h2>
                    </div>

                    {/* Tabs */}
                    <ul className="nav nav-pills justify-content-between notice-tabs mb-5">
                        {visibleCategories && visibleCategories.length > 0 ? (
                            visibleCategories.map((cat, index) => (
                                <li className="nav-item" key={cat.id || index}>
                                    <a
                                        className={`nav-link ${index === 0 ? 'active' : ''}`}
                                        data-bs-toggle="pill"
                                        href={`#tab${index + 1}`}
                                    >
                                        {cat.name}
                                    </a>
                                </li>
                            ))
                        ) : (
                            <li className="nav-item">
                                <a className="nav-link active" data-bs-toggle="pill" href="#tab1">General Club Notice</a>
                            </li>
                        )}
                    </ul>

                    {/* Notice Content - Blurred for non-logged-in users */}
                    <div style={{ position: 'relative' }}>
                        {/* If guest has no general/rules categories available, show login overlay; otherwise show allowed categories */}
                        {(!isLoggedIn && (!visibleCategories || visibleCategories.length === 0)) && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10,
                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: '15px'
                            }}>
                                <div style={{
                                    textAlign: 'center',
                                    padding: '30px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    borderRadius: '15px',
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                                }}>
                                    <i className="fa fa-lock" style={{ fontSize: '40px', color: '#2A65E5', marginBottom: '15px' }}></i>
                                    <h4 style={{ color: '#333', marginBottom: '10px' }}>Members Only Content</h4>
                                    <p style={{ color: '#666', marginBottom: '20px' }}>Please log in to view notices and announcements</p>
                                    <button
                                        onClick={handleShowNotices}
                                        className="btn btn-primary"
                                        style={{
                                            padding: '12px 30px',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            borderRadius: '25px'
                                        }}
                                    >
                                        <i className="fa fa-sign-in me-2"></i>
                                        Login to View
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Tab Content */}
                        <div className="tab-content">
                            {visibleCategories && visibleCategories.length > 0 ? (
                                visibleCategories.map((cat, index) => {
                                    // Determine if this category should be blurred for guests
                                    const shouldBlur = !isLoggedIn && isBlurredForGuest(cat.name) && !isFullyVisibleForGuest(cat.name);
                                    return (
                                        <div
                                            className={`tab-pane fade ${index === 0 ? 'show active' : ''}`}
                                            id={`tab${index + 1}`}
                                            key={cat.id || index}
                                            style={{ position: 'relative' }}
                                        >
                                            {/* Blur overlay for blurred categories (e.g., Club Report) */}
                                            {shouldBlur && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    zIndex: 10,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                                    backdropFilter: 'blur(8px)',
                                                    borderRadius: '15px'
                                                }}>
                                                    <div style={{
                                                        textAlign: 'center',
                                                        padding: '30px',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                        borderRadius: '15px',
                                                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                                                    }}>
                                                        <i className="fa fa-lock" style={{ fontSize: '40px', color: '#2A65E5', marginBottom: '15px' }}></i>
                                                        <h4 style={{ color: '#333', marginBottom: '10px' }}>Members Only Content</h4>
                                                        <p style={{ color: '#666', marginBottom: '20px' }}>Please log in to view {cat.name}</p>
                                                        <button
                                                            onClick={handleShowNotices}
                                                            className="btn btn-primary"
                                                            style={{
                                                                padding: '12px 30px',
                                                                fontSize: '16px',
                                                                fontWeight: '600',
                                                                borderRadius: '25px'
                                                            }}
                                                        >
                                                            <i className="fa fa-sign-in me-2"></i>
                                                            Login to View
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {/* Actual content (blurred behind overlay if shouldBlur) */}
                                            <div style={shouldBlur ? { filter: 'blur(5px)', pointerEvents: 'none', userSelect: 'none' } : {}}>
                                                {loading ? (
                                                    <div className="row g-4">
                                                        <div className="col-12 text-center py-5">
                                                            <div className="spinner-border text-primary" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : displayNotices && displayNotices.filter(n => n.category_id === cat.id).length > 0 ? (
                                                    <NoticeSlider
                                                        notices={displayNotices.filter(n => n.category_id === cat.id)}
                                                        formatDate={formatDate}
                                                        logo={logo}
                                                    />
                                                ) : (
                                                    <div className="row g-4">
                                                        <div className="col-12 text-center py-5">
                                                            <p className="text-muted">No notices available in this category</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="tab-pane fade show active" id="tab1">
                                    <div className="row g-4">
                                        <div className="col-12 text-center py-5">
                                            <p className="text-muted">No categories available</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}

const NoticeSlider = ({ notices, formatDate, logo }) => {
    const swiperRef = useRef(null);
    const [currentSlide, setCurrentSlide] = useState(1);

    useEffect(() => {
        if (swiperRef.current) {
            const swiper = new Swiper(swiperRef.current, {
                modules: [Navigation],
                navigation: {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev'
                },
                loop: true,
                slidesPerView: 2,
                spaceBetween: 30,
                observer: true,
                observeParents: true,
                speed: 800,
                  watchOverflow: false,

                // autoplay: {
                //     delay: 5000,
                //     disableOnInteraction: false,
                // },
                breakpoints: {
                    992: { slidesPerView: 2 },
                    768: { slidesPerView: 2 },
                    0: { slidesPerView: 1 }
                }
            });

            // Update slide counter when slide changes
            swiper.on('slideChange', () => {
                setCurrentSlide(swiper.realIndex + 1);
            });

            return () => {
                if (swiper) swiper.destroy();
            };
        }
    }, []);

    return (
        <div className="row">
            <div className="col-lg-12">
                <div className="swiper notice-active" ref={swiperRef}>
                    <div className="swiper-wrapper">
                        {notices.map((notice, noticeIndex) => {
                            const dateInfo = formatDate(notice.notice_date);
                            return (
                                <div className="swiper-slide" key={notice.id || noticeIndex}>
                                    <div className="notice-card d-flex align-items-center h-100"> {/* Added h-100 for equal height */}
                                        {
                                            notice?.category_id === 2 ? (
                                                <figure className="notice-logo">
                                                    <img src={logo} alt="Logo" className='img-fluid' />
                                                </figure>
                                            ) : (
                                                <div className="date-box">
                                                    <h3>{dateInfo.day}</h3>
                                                    <p>{dateInfo.month}</p>
                                                    <span>{dateInfo.year}</span>
                                                </div>
                                            )
                                        }
                                        <div className="notice-content">
                                            <h4>{notice?.title}</h4>
                                            <p>{notice.description}</p>
                                            {notice.pdf_url && (
                                                <div className='d-flex justify-content-start align-item-center mt-3'>
                                                    <a href={notice.pdf_url.startsWith('http') ? notice.pdf_url : `${REACT_APP_BRANDURL}${notice.pdf_url}`} target="_blank" rel="noopener noreferrer" className='btn btn-danger'>Download PDF</a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                {/* Navigation */}
                <div className="swiper-navigation mt-4">
                    <div className="swiper-button-prev custom-prev"></div>
                    <div className="member-counter">
                        <span id="current-slide">{currentSlide}</span> / <span id="total-slide" style={{ color: '#E89C05', fontSize: '20px' }}>{notices.length}</span>
                    </div>
                    <div className="swiper-button-next custom-next"></div>
                </div>
            </div>
        </div>
    );
};
